import { FC, ReactNode } from "react";
import { useMessages } from "../../context/i18n";
import { trial as enTrial } from "../../dictionaries/en/trial";
import MediaBlock from "../blocks/MediaBlock";
import TrustedBy from "../TrustedBy";
import Typography from "../Typography";
import VideoInput from "../VideoInput";

type SourcePlatformBadgeProps = {
  name: string;
};

// Lightweight pill row used under the hero on page 01 to surface
// supported URL sources (YouTube, Vimeo, Twitch, etc.). The actual
// brand SVGs already ship in `public/svg/logos/`; the master spec
// allows a text-only fallback when no per-page asset is wired in.
const SourcePlatformBadge: FC<SourcePlatformBadgeProps> = ({ name }) => (
  <span className="inline-flex items-center text-xs font-medium text-slate-500 px-2 py-1 rounded-full border border-slate-200 bg-white">
    {name}
  </span>
);

type HeroProps = {
  h1: ReactNode;
  subhead: ReactNode;
  videoSrc: string;
  videoAlt: string;
  videoInputPlaceholder?: string;
  sourcePlatformBadges?: string[];
};

const Hero: FC<HeroProps> = ({
  h1,
  subhead,
  videoSrc,
  videoAlt,
  videoInputPlaceholder,
  sourcePlatformBadges,
}) => {
  const trial = useMessages("trial", enTrial);
  return (
    <section className="text-center text-slate-950 flex flex-col items-center max-w-5xl mx-auto px-3 my-16">
      <TrustedBy className="mb-8" />
      <Typography variant="h1" component="h1">
        {h1}
      </Typography>
      <Typography variant="body1" className="mt-8 text-slate-600 max-w-3xl">
        {subhead}
      </Typography>
      <VideoInput
        placeholderText={videoInputPlaceholder}
        className="mt-12 w-full shadow-2xl max-w-xl mx-auto"
      />
      <Typography variant="caption" className="text-slate-500 mt-4">
        {trial.cta}
      </Typography>
      {sourcePlatformBadges && sourcePlatformBadges.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {sourcePlatformBadges.map((name) => (
            <SourcePlatformBadge key={name} name={name} />
          ))}
        </div>
      )}
      <div className="mt-16 w-full max-w-5xl">
        <MediaBlock
          data={{ type: "video", src: videoSrc, alt: videoAlt }}
          className="shadow-2xl shadow-slate-300 ring-2 ring-slate-300/50 bg-slate-200 mx-auto"
        />
      </div>
    </section>
  );
};

export default Hero;
